import { useEffect, useState } from "react";
import { Plus, Trash2, Star, Quote, Pencil, Check, X, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/ImageUpload";
import BulkTestimonialsUploadModal from "@/components/BulkTestimonialsUploadModal";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { LANDING_ADMIN } from "@/constants/testIds";

const EMPTY_FORM = { type: "text", patient_name: "", text: "", image_url: null, service_id: "none", rating: 5 };

/** Card de Ajustes: testimonios de pacientes mostrados en la landing, como
 * texto maquetado o como captura de pantalla de una reseña (Google/redes). */
export default function TestimonialsSettingsCard() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const { toast } = useToast();

  const load = () => api.get("/landing/testimonials").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const serviceName = (id) => services.find((s) => s.id === id)?.name;

  const handleAdd = async () => {
    if (!form.patient_name.trim()) return;
    if (form.type === "text" && !form.text.trim()) {
      toast({ title: "Escribí el texto del testimonio", variant: "destructive" });
      return;
    }
    if (form.type === "image" && !form.image_url) {
      toast({ title: "Subí la captura de pantalla de la reseña", variant: "destructive" });
      return;
    }
    try {
      await api.post("/landing/testimonials", {
        patient_name: form.patient_name,
        text: form.type === "text" ? form.text : null,
        image_url: form.type === "image" ? form.image_url : null,
        service_id: form.service_id === "none" ? null : form.service_id,
        rating: form.type === "text" ? form.rating : null,
      });
      setForm(EMPTY_FORM);
      setAdding(false);
      load();
      toast({ title: "Testimonio agregado" });
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const toggleActive = async (item) => {
    await api.put(`/landing/testimonials/${item.id}`, { is_active: !item.is_active });
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/landing/testimonials/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const startEditName = (item) => { setEditingId(item.id); setEditingName(item.patient_name); };

  const saveEditName = async () => {
    if (!editingName.trim()) return;
    try {
      await api.put(`/landing/testimonials/${editingId}`, { patient_name: editingName.trim() });
      setEditingId(null);
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={LANDING_ADMIN.testimonialsCard}>
      {/* Fix AI (2026-10-23): en el celular los botones pasan a la linea de abajo en vez de salirse de la pantalla */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-foreground">Testimonios</h2>
          <p className="text-sm text-muted-foreground mt-1">Reseñas de pacientes que se muestran en la landing pública, como texto o como captura de pantalla.</p>
        </div>
        {!adding && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setBulkOpen(true)} data-testid={LANDING_ADMIN.testimonialBulkUploadButton}>
              <UploadCloud className="w-4 h-4" /> Subir en lote
            </Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setAdding(true)} data-testid={LANDING_ADMIN.testimonialAddButton}>
              <Plus className="w-4 h-4" /> Nuevo
            </Button>
          </div>
        )}
      </div>

      {adding && (
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex gap-2" data-testid={LANDING_ADMIN.testimonialTypeToggle}>
            <Button type="button" size="sm" variant={form.type === "text" ? "default" : "outline"} className={form.type === "text" ? "bg-brand hover:bg-brand-hover text-white" : ""} onClick={() => setForm((f) => ({ ...f, type: "text" }))}>Texto</Button>
            <Button type="button" size="sm" variant={form.type === "image" ? "default" : "outline"} className={form.type === "image" ? "bg-brand hover:bg-brand-hover text-white" : ""} onClick={() => setForm((f) => ({ ...f, type: "image" }))}>Captura de pantalla</Button>
          </div>
          <Input placeholder="Nombre de la paciente" value={form.patient_name} onChange={(e) => setForm((f) => ({ ...f, patient_name: e.target.value }))} data-testid={LANDING_ADMIN.testimonialNameInput} />
          {form.type === "text" ? (
            <>
              <Textarea placeholder="Testimonio" rows={3} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} data-testid={LANDING_ADMIN.testimonialTextInput} />
              <Input type="number" min={1} max={5} placeholder="Calificación (1-5)" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))} data-testid={LANDING_ADMIN.testimonialRatingInput} className="w-40" />
            </>
          ) : (
            <ImageUpload currentUrl={form.image_url} onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} placeholderUrl={null} testId={LANDING_ADMIN.testimonialImageUpload} shape="square" />
          )}
          <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
            <SelectTrigger data-testid={LANDING_ADMIN.testimonialServiceSelect}><SelectValue placeholder="Relacionar con un tratamiento (opcional)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sin tratamiento asociado</SelectItem>
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={handleAdd} data-testid={LANDING_ADMIN.testimonialSaveButton}>Guardar</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setForm(EMPTY_FORM); }} data-testid={LANDING_ADMIN.testimonialCancelButton}>Cancelar</Button>
          </div>
        </div>
      )}

      {items.length === 0 && !adding && (
        <div className="text-center py-6">
          <Quote className="w-6 h-6 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no hay testimonios cargados.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="flex items-start justify-between gap-3 border border-border rounded-lg p-3" data-testid={`${LANDING_ADMIN.testimonialRowPrefix}${i.id}`}>
            <div className="min-w-0 flex items-start gap-3">
              {i.image_url && <img src={getFileUrl(i.image_url)} alt={i.patient_name} className="w-12 h-12 rounded object-cover shrink-0" />}
              <div className="min-w-0">
                {editingId === i.id ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="h-7 text-sm w-40"
                      data-testid={`${LANDING_ADMIN.testimonialNameEditInputPrefix}${i.id}`}
                    />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={saveEditName} data-testid={`${LANDING_ADMIN.testimonialNameEditSaveButtonPrefix}${i.id}`}>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingId(null)} data-testid={`${LANDING_ADMIN.testimonialNameEditCancelButtonPrefix}${i.id}`}>
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{i.patient_name}</p>
                    <button type="button" onClick={() => startEditName(i)} data-testid={`${LANDING_ADMIN.testimonialEditButtonPrefix}${i.id}`}>
                      <Pencil className="w-3 h-3 text-muted-foreground hover:text-brand" />
                    </button>
                    {i.rating && (
                      <span className="flex items-center gap-0.5">
                        {Array.from({ length: i.rating }).map((_, idx) => <Star key={idx} className="w-3 h-3 fill-brand text-brand" />)}
                      </span>
                    )}
                  </div>
                )}
                {i.text && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{i.text}</p>}
                {i.service_id && serviceName(i.service_id) && <p className="text-[11px] font-medium text-brand mt-1">{serviceName(i.service_id)}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={i.is_active} onCheckedChange={() => toggleActive(i)} data-testid={`${LANDING_ADMIN.testimonialActiveTogglePrefix}${i.id}`} />
              <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(i)} data-testid={`${LANDING_ADMIN.testimonialDeleteButtonPrefix}${i.id}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este testimonio?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkTestimonialsUploadModal open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
    </div>
  );
}
